import React from 'react';
import type { WidgetProps } from '../types';
import { getCompatibleTypes } from '../types';
import { WidgetWrapper } from '../WidgetWrapper';

/**
 * Registro por tipo de widget (WidgetDefinition.tipo).
 * Resuelve el componente correcto según el tipo del widget en BD.
 * Para widgets custom por ID, el host puede inyectar un registro adicional
 * via la prop customWidgetRegistry de DashboardPage.
 */
const typeRegistry: Record<string, React.LazyExoticComponent<React.FC<WidgetProps>>> = {
  'kpi': React.lazy(() => import('../widgets/KpiVentasHoy')),
  'chart_line': React.lazy(() => import('../widgets/ChartLine')),
  'chart_bar_vertical': React.lazy(() => import('../widgets/ChartVentasMensual')),
  'chart_bar_horizontal': React.lazy(() => import('../widgets/ChartBarHorizontal')),
  'chart_donut': React.lazy(() => import('../widgets/ChartDonut')),
  'table': React.lazy(() => import('../widgets/TableStockCritico')),
  'image': React.lazy(() => import('../widgets/StaticImageWidget')),
};

export type WidgetRegistry = Record<string, React.LazyExoticComponent<React.FC<WidgetProps>>>;

export interface LazyWidgetProps extends WidgetProps {
  onRemove?: (id: string) => void;
  /** Registro custom del host: mapea widget_id → componente React. */
  customWidgetRegistry?: WidgetRegistry;
}

/**
 * Componente que se encarga de resolver dinámicamente qué archivo React
 * cargar basado en el `widgetId` que viene de la BD.
 * Si el usuario tiene un override de chartType en config_json (panel de personalización),
 * se usa ese tipo siempre que sea compatible con la familia del tipo original.
 */
export const LazyWidget: React.FC<LazyWidgetProps> = (props) => {
  const { definition, preference, customWidgetRegistry } = props;

  // Override de tipo desde config_json del usuario (panel de personalización)
  const overrideType = preference.config_json?.chartType as string | undefined;
  const effectiveType = overrideType && getCompatibleTypes(definition.tipo).includes(overrideType)
    ? overrideType
    : definition.tipo;

  const Component = customWidgetRegistry?.[definition.widget_id] ?? typeRegistry[effectiveType];

  // Si el widget no está registrado en el mapa (ej. no existe el archivo), mostramos un fallback seguro
  if (!Component) {
    return (
      <WidgetWrapper {...props} globalFilters={{ period: props.period }}>
        <div className="flex items-center justify-center h-full p-4 text-center">
          <p className="text-sm text-gray-500">
            Widget <span className="font-semibold text-gray-700">"{definition.widget_id}"</span> no disponible o archivo no encontrado.
          </p>
        </div>
      </WidgetWrapper>
    );
  }

  // Componente válido encontrado en el registro dinámico
  return (
    <WidgetWrapper {...props} globalFilters={{ period: props.period }}>
      <Component {...props} />
    </WidgetWrapper>
  );
};
